import { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Users } from 'lucide-react';
import Layout from '../components/Layout';
import { Card, Button, Spinner } from '../components/ui';
import { teamsApi } from '../lib/api';
import type { AcceptedInvite } from '../types/teams';

export default function TeamAccept() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [team, setTeam] = useState<AcceptedInvite | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;

    if (!token) {
      setStatus('error');
      setErrorMessage('No invite token found in the link. Please use the link from your invitation email.');
      return;
    }

    (async () => {
      try {
        const res = await teamsApi.acceptInvite(token);
        if (res.success && res.data) {
          setTeam(res.data.team);
          setStatus('success');
        } else {
          setErrorMessage(res.message || 'This invite could not be accepted. It may have expired or been revoked.');
          setStatus('error');
        }
      } catch {
        setErrorMessage('Something went wrong while accepting the invite. Please try again.');
        setStatus('error');
      }
    })();
  }, [token]);

  return (
    <Layout>
      <div className="flex items-center justify-center min-h-[60vh]">
        <Card padded className="w-full max-w-md text-center">
          {status === 'loading' && (
            <div className="py-8 flex flex-col items-center gap-4">
              <Spinner />
              <p className="text-sm text-ink-2">Accepting your team invite...</p>
            </div>
          )}

          {status === 'success' && team && (
            <div className="py-4 flex flex-col items-center">
              <div className="w-[64px] h-[64px] rounded-[18px] bg-success-soft text-success flex items-center justify-center mb-4">
                <CheckCircle className="w-8 h-8" />
              </div>
              <h2 className="font-display font-bold text-[20px] text-ink">
                Welcome to {team.name}!
              </h2>
              <p className="text-sm text-ink-2 mt-2">
                You joined as <span className="font-semibold capitalize">{team.role}</span>.
                Connections shared with this team are now available in your Vault.
              </p>
              <Link to="/teams" className="mt-5">
                <Button icon={Users}>Go to Teams</Button>
              </Link>
            </div>
          )}

          {status === 'error' && (
            <div className="py-4 flex flex-col items-center">
              <div className="w-[64px] h-[64px] rounded-[18px] bg-danger-soft text-danger flex items-center justify-center mb-4">
                <XCircle className="w-8 h-8" />
              </div>
              <h2 className="font-display font-bold text-[20px] text-ink">Invite not accepted</h2>
              <p className="text-sm text-ink-2 mt-2">{errorMessage}</p>
              <Link to="/teams" className="mt-5">
                <Button variant="ghost" icon={Users}>Go to Teams</Button>
              </Link>
            </div>
          )}
        </Card>
      </div>
    </Layout>
  );
}
